/**
 * SwUpdateBanner (Plan 01-15, Task 2).
 *
 * UI-SPEC §"Interaction Contracts §4 — Phone & desktop":
 *   When `hasUpdate && deviceClass !== 'tablet'`, render a fixed bottom
 *   banner. Tap anywhere → sendSkipWaiting(reg). Plan 01-08's
 *   `controllerchange` listener handles the actual `location.reload()` once
 *   the active SW hands off to the new one — banner just kicks the chain.
 *
 * Visual contract (UI-SPEC §4):
 *   - fixed bottom-0 left-0 right-0, h-16 (64px)
 *   - bg-ink, text-sheet, rounded-t-lg, shadow-lg
 *   - 13px label (font-semibold) + 13px body (opacity-80)
 *   - chevron `›` right-aligned (visual only, aria-hidden)
 *   - role=status + aria-live=polite (assistive tech announces non-disruptively)
 *   - NOT dismissable — FND-02 reliability requirement; if the user ignores
 *     the banner, the next mount re-renders it because `hasUpdate` is still
 *     true. The whole point of this surface is that updates happen.
 *
 * Why click + pointerdown both: pointerdown fires sooner on touch which
 * matters for a phone gesture, but click is the universal "I tapped this"
 * fallback. Calling sendSkipWaiting twice is harmless — `reg.waiting` is
 * already-acted-on by the second call (no-op postMessage on the same
 * worker). Browser deduplication on rapid pointer→click is also reliable
 * enough that the test only counts a single click event.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwUpdateStore } from './SwUpdateStore';
import { useDashboard } from '../dashboard/DashboardStore';
import { sendSkipWaiting } from '../../lib/sw-register';

const CHEVRON = '›'; // single right-pointing angle quotation mark — visual only, locale-invariant

export function SwUpdateBanner(): ReactElement | null {
  const { t } = useTranslation();
  const hasUpdate = useSwUpdateStore((s) => s.hasUpdate);
  const waitingReg = useSwUpdateStore((s) => s.waitingReg);
  const deviceClass = useDashboard((s) => s.deviceClass);

  if (!hasUpdate || deviceClass === 'tablet') return null;

  const onTap = (): void => {
    if (waitingReg) sendSkipWaiting(waitingReg);
    // Plan 08 'controllerchange' listener triggers location.reload() exactly
    // once after the new SW takes control.
  };

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={onTap}
      className="fixed bottom-0 left-0 right-0 h-16 bg-ink text-sheet rounded-t-lg shadow-lg flex items-center justify-between px-6 cursor-pointer transition-transform duration-300"
      data-testid="sw-update-banner"
    >
      <div>
        <p className="text-sm font-semibold">{t('sw.update.label')}</p>
        <p className="text-sm opacity-80">{t('sw.update.body')}</p>
      </div>
      <span aria-hidden="true" className="text-sheet text-lg">
        {CHEVRON}
      </span>
    </div>
  );
}
